import React, { useEffect, useRef, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ items = [], index, onClose, onChange }) {
  const closeBtnRef = useRef(null);
  const touchStartX = useRef(null);

  const prev = useCallback(() => {
    if (items.length === 0) return;
    onChange((index - 1 + items.length) % items.length);
  }, [index, items.length, onChange]);

  const next = useCallback(() => {
    if (items.length === 0) return;
    onChange((index + 1) % items.length);
  }, [index, items.length, onChange]);

  // Keyboard handlers
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  // Focus + scroll lock
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  if (index < 0 || index >= items.length) return null;
  const item = items[index];

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 60) { dx > 0 ? prev() : next(); }
    touchStartX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex flex-col"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={item.alt || item.title || "Portfolio image"}
      data-testid="lightbox"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm text-white/70" data-testid="lightbox-counter">{index + 1} / {items.length}</p>
        <button
          ref={closeBtnRef}
          onClick={onClose}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          aria-label="Close lightbox"
          data-testid="lightbox-close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Image stage */}
      <div
        className="flex-1 relative flex items-center justify-center px-4 md:px-16"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="lightbox-stage"
      >
        {items.length > 1 && (
          <button
            onClick={prev}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 h-11 w-11 md:h-12 md:w-12 grid place-items-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <img
          src={item.image}
          alt={item.alt || item.title || "Portfolio image"}
          className="max-h-[78vh] max-w-full w-auto h-auto object-contain select-none"
          draggable={false}
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-image"
        />
        {items.length > 1 && (
          <button
            onClick={next}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 h-11 w-11 md:h-12 md:w-12 grid place-items-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Caption */}
      <div className="px-4 py-4 md:py-5 text-center text-white" onClick={(e) => e.stopPropagation()}>
        <p className="eyebrow text-white/70 text-[0.7rem]">{item.category}</p>
        {item.title && <p className="mt-1 heading text-lg md:text-2xl">{item.title}</p>}
        {item.description && <p className="mt-1 text-sm text-white/80 max-w-xl mx-auto">{item.description}</p>}
      </div>
    </div>
  );
}
